// ═══════════════════════════════════════════════════════
// SIDEBAR — navegação lateral do portal do cliente
// Brand: dark-native, copper accent, SVG icons, a11y
// Grouped accordion navigation with collapsed popover
// ═══════════════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useIsMobile } from "../shared/hooks.js";
import { NAV, PERFIL_NAV, findGroupForPage } from "../shared/navConfig.js";
import { safeText } from "../shared/formatters.js";
import { RADIUS, TRANSITION, SP, TYPE, SHADOW, GRADIENT } from "../shared/theme.js";
import {
  IconBell,
  IconSun,
  IconMoon,
  IconLogOut,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconUser,
  IconEye,
  IconEyeOff,
} from "../shared/icons.jsx";

// ── Brand palette (sidebar always dark) ───────────────
const S = {
  bg: "linear-gradient(180deg, #0F172A 0%, #0A1628 100%)",
  accent: "#19376D",
  textActive: "#F6F3EE",
  textNorm: "rgba(246,243,238,0.55)",
  textHover: "rgba(246,243,238,0.85)",
  textWhite: "#F6F3EE",
  divider: "rgba(246,243,238,0.07)",
  cardBg: "rgba(246,243,238,0.06)",
  badge: "#DC2626",
  groupActive: "rgba(25,55,109,0.15)",
};

export default function Sidebar({
  active,
  setActive,
  onLogout,
  col,
  setCol,
  dark,
  setDark,
  T: _T,
  client,
  notifications,
  onNotifClick,
  mobileOpen,
  setMobileOpen,
  privacyMode,
  setPrivacyMode,
}) {
  const isMobile = useIsMobile();
  const sidebarWidth = isMobile ? 272 : col ? 72 : 248;
  const isHidden = isMobile && !mobileOpen;
  const unread = notifications?.filter((n) => !n.read).length || 0;

  // ── Group accordion state ───────────────────────────
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    NAV.forEach((item) => {
      if (item.isGroup) init[item.id] = !!item.defaultOpen;
    });
    return init;
  });
  const toggleGroup = useCallback(
    (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );

  // Auto-expand group when active page changes
  useEffect(() => {
    const group = findGroupForPage(active);
    if (group && !openGroups[group.id]) {
      setOpenGroups((prev) => ({ ...prev, [group.id]: true }));
    }
  }, [active]);

  // ── Collapsed popover state ─────────────────────────
  const [popoverGroup, setPopoverGroup] = useState(null);
  const popoverRef = useRef(null);
  const popoverTriggerRef = useRef(null);

  // Close popover on click outside
  useEffect(() => {
    if (!popoverGroup) return;
    const handler = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        popoverTriggerRef.current &&
        !popoverTriggerRef.current.contains(e.target)
      ) {
        setPopoverGroup(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popoverGroup]);

  const navigateTo = useCallback(
    (pageId) => {
      setActive(pageId);
      if (isMobile) setMobileOpen(false);
      setPopoverGroup(null);
    },
    [setActive, isMobile, setMobileOpen]
  );

  // ── Visibility filter ───────────────────────────────
  const isVisible = useCallback(
    (item) => !item.hidden && (!item.onlyClients || item.onlyClients.includes(client?.id)),
    [client?.id]
  );

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.6)",
            zIndex: 19,
            backdropFilter: "blur(4px)",
            transition: TRANSITION.normal,
          }}
        />
      )}

      <aside
        className="no-print lpa-sidebar"
        data-tour="sidebar"
        role="navigation"
        aria-label="Menu principal"
        style={{
          width: sidebarWidth,
          minHeight: "100vh",
          background: S.bg,
          display: "flex",
          flexDirection: "column",
          transition:
            "transform 0.3s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1)",
          position: isMobile ? "fixed" : "relative",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 20,
          flexShrink: 0,
          transform: isHidden ? "translateX(-100%)" : "translateX(0)",
          boxShadow: isMobile && mobileOpen ? "4px 0 32px rgba(0,0,0,0.4)" : "none",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: col ? `${SP[5]} 0` : `${SP[5]} ${SP[4]}`,
            display: "flex",
            alignItems: "center",
            gap: SP[3],
            borderBottom: `1px solid ${S.divider}`,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: RADIUS.sm,
              background: S.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: TYPE.heavy,
              fontFamily: TYPE.fontSerif,
              fontSize: TYPE.md,
              color: S.textActive,
              flexShrink: 0,
              margin: col ? "0 auto" : 0,
            }}
          >
            L
          </div>
          {!col && (
            <div>
              <div
                style={{
                  fontSize: TYPE.md,
                  fontWeight: TYPE.heavy,
                  color: S.textWhite,
                  fontFamily: TYPE.fontSans,
                  letterSpacing: "0.5px",
                }}
              >
                LPA <span style={{ color: S.accent }}>Wealth</span>
              </div>
              <div
                style={{
                  fontSize: TYPE.xs,
                  color: "rgba(246,243,238,0.3)",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  fontFamily: TYPE.fontSans,
                }}
              >
                Investment Advisory
              </div>
            </div>
          )}
        </div>

        {/* Collapse toggle (desktop) */}
        {!isMobile && (
          <button
            onClick={() => setCol(!col)}
            aria-label={col ? "Expandir menu" : "Recolher menu"}
            style={{
              position: "absolute",
              right: -13,
              top: SP[6],
              width: 26,
              height: 26,
              borderRadius: RADIUS.full,
              background: S.accent,
              border: "none",
              cursor: "pointer",
              color: S.textActive,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: SHADOW.sm,
              transition: TRANSITION.fast,
              zIndex: 21,
            }}
          >
            {col ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
          </button>
        )}

        {/* Client Card */}
        {!col && client && (
          <div
            style={{
              margin: `${SP[3]} ${SP[3]} 0`,
              padding: SP[3],
              background: S.cardBg,
              borderRadius: RADIUS.md,
              transition: TRANSITION.normal,
            }}
          >
            <div
              style={{
                fontSize: TYPE.xs,
                color: S.accent,
                fontWeight: TYPE.bold,
                letterSpacing: "1px",
                textTransform: "uppercase",
                marginBottom: SP[1],
                fontFamily: TYPE.fontSans,
              }}
            >
              Cliente
            </div>
            <div
              style={{
                fontSize: TYPE.sm,
                fontWeight: TYPE.bold,
                color: S.textWhite,
                fontFamily: TYPE.fontSans,
              }}
            >
              {safeText(client.shortName || client.name, "sidebar-client-name")}
            </div>
            <div style={{ fontSize: TYPE.xs, color: S.textNorm, fontFamily: TYPE.fontMono }}>
              {safeText(client.code, "sidebar-client-code")}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav
          style={{ flex: 1, padding: `${SP[3]} ${SP[2]}`, overflowY: "auto" }}
          aria-label="Navegação do portal"
        >
          {NAV.filter((item) => {
            if (item.isGroup) {
              // Show group if at least one child is visible
              return item.children.some(isVisible);
            }
            return isVisible(item);
          }).map((item) =>
            item.isGroup ? (
              <SidebarGroup
                key={item.id}
                group={item}
                active={active}
                col={col}
                openGroups={openGroups}
                toggleGroup={toggleGroup}
                navigateTo={navigateTo}
                isVisible={isVisible}
                popoverGroup={popoverGroup}
                setPopoverGroup={setPopoverGroup}
                popoverRef={popoverRef}
                popoverTriggerRef={popoverTriggerRef}
              />
            ) : (
              <NavItem
                key={item.id}
                item={item}
                active={active === item.id}
                col={col}
                onClick={() => navigateTo(item.id)}
              />
            )
          )}
        </nav>

        {/* Footer */}
        <div
          style={{
            padding: col ? `${SP[3]} ${SP[2]}` : `${SP[3]} ${SP[3]}`,
            borderTop: `1px solid ${S.divider}`,
          }}
        >
          {!col && client && (
            <div style={{ marginBottom: SP[3] }}>
              <div
                style={{
                  fontSize: TYPE.xs,
                  color: "rgba(246,243,238,0.3)",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontFamily: TYPE.fontSans,
                }}
              >
                Assessor
              </div>
              <div
                style={{
                  fontSize: TYPE.sm,
                  color: S.textHover,
                  fontWeight: TYPE.semibold,
                  fontFamily: TYPE.fontSans,
                }}
              >
                {safeText(client?.advisor || "\u2013", "sidebar-advisor")}
              </div>
              <div
                style={{
                  fontSize: TYPE.xs,
                  color: "rgba(246,243,238,0.3)",
                  fontFamily: TYPE.fontMono,
                }}
              >
                {client?.lastUpdate
                  ? `Ref. ${safeText(client.lastUpdate, "sidebar-lastUpdate")}`
                  : ""}
              </div>
            </div>
          )}
          <div data-tour="profile-button">
            <SidebarBtn
              onClick={() => navigateTo(PERFIL_NAV.id)}
              col={col}
              icon={<IconUser size={16} />}
              label="Meu Perfil"
              ariaLabel="Ir para Meu Perfil"
              isActive={active === PERFIL_NAV.id}
            />
          </div>
          <SidebarBtn
            onClick={onNotifClick}
            col={col}
            icon={<IconBell size={16} />}
            label="Notificações"
            badge={unread}
            ariaLabel={`Notificações${unread ? ` (${unread} não lidas)` : ""}`}
          />
          <div data-tour="privacy-toggle">
            <SidebarBtn
              onClick={() => setPrivacyMode(!privacyMode)}
              col={col}
              icon={privacyMode ? <IconEyeOff size={16} /> : <IconEye size={16} />}
              label={privacyMode ? "Mostrar Valores" : "Ocultar Valores"}
              ariaLabel={
                privacyMode ? "Mostrar valores financeiros" : "Ocultar valores financeiros"
              }
            />
          </div>
          <div data-tour="dark-mode-toggle">
            <SidebarBtn
              onClick={() => setDark(!dark)}
              col={col}
              icon={dark ? <IconSun size={16} /> : <IconMoon size={16} />}
              label={dark ? "Modo Claro" : "Modo Escuro"}
              ariaLabel={dark ? "Alternar para modo claro" : "Alternar para modo escuro"}
            />
          </div>
          <SidebarBtn
            onClick={onLogout}
            col={col}
            icon={<IconLogOut size={16} />}
            label="Sair"
            ariaLabel="Encerrar sessão"
            last
          />
        </div>
      </aside>
    </>
  );
}

// ═══════════════════════════════════════════════════════
// NAV ITEM — single page button
// ═══════════════════════════════════════════════════════
function NavItem({ item, active, col, onClick, indent = false }) {
  const NavIcon = item.Icon;
  return (
    <button
      onClick={onClick}
      aria-label={`Navegar para ${item.label}`}
      aria-current={active ? "page" : undefined}
      title={col ? item.label : undefined}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: SP[3],
        padding: col ? `${SP[2]} 0` : `${SP[2]} ${SP[3]}`,
        paddingLeft: indent && !col ? SP[7] : col ? 0 : SP[3],
        justifyContent: col ? "center" : "flex-start",
        borderRadius: RADIUS.sm,
        border: "none",
        cursor: "pointer",
        marginBottom: "2px",
        background: active ? S.accent : "transparent",
        color: active ? S.textActive : S.textNorm,
        fontWeight: active ? TYPE.bold : TYPE.medium,
        fontSize: indent ? TYPE.sm : TYPE.base,
        fontFamily: TYPE.fontSans,
        transition: TRANSITION.fast,
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = S.cardBg;
          e.currentTarget.style.color = S.textHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = S.textNorm;
        }
      }}
      onFocus={(e) => {
        e.currentTarget.style.outline = `2px solid ${S.accent}`;
        e.currentTarget.style.outlineOffset = "-2px";
      }}
      onBlur={(e) => {
        e.currentTarget.style.outline = "none";
      }}
    >
      {NavIcon && <NavIcon size={col ? 20 : indent ? 16 : 18} />}
      {!col && <span>{item.label}</span>}
    </button>
  );
}
NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool,
  col: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  indent: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════
// SIDEBAR GROUP — accordion header + children
// ═══════════════════════════════════════════════════════
function SidebarGroup({
  group,
  active,
  col,
  openGroups,
  toggleGroup,
  navigateTo,
  isVisible,
  popoverGroup,
  setPopoverGroup,
  popoverRef,
  popoverTriggerRef,
}) {
  const isOpen = openGroups[group.id];
  const GroupIcon = group.Icon;
  const visibleChildren = group.children.filter(isVisible);
  const hasActiveChild = visibleChildren.some((c) => c.id === active);
  const triggerRef = useRef(null);

  // ── Collapsed mode: show popover ──────────────────
  if (col) {
    const isPopoverOpen = popoverGroup === group.id;
    return (
      <div style={{ position: "relative" }}>
        <button
          ref={(el) => {
            triggerRef.current = el;
            if (isPopoverOpen) popoverTriggerRef.current = el;
          }}
          onClick={() => setPopoverGroup(isPopoverOpen ? null : group.id)}
          aria-label={group.label}
          title={group.label}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: `${SP[2]} 0`,
            borderRadius: RADIUS.sm,
            border: "none",
            cursor: "pointer",
            marginBottom: "2px",
            background: hasActiveChild ? S.groupActive : "transparent",
            color: hasActiveChild ? S.accent : S.textNorm,
            transition: TRANSITION.fast,
            outline: "none",
          }}
          onMouseEnter={(e) => {
            if (!hasActiveChild) {
              e.currentTarget.style.background = S.cardBg;
              e.currentTarget.style.color = S.textHover;
            }
          }}
          onMouseLeave={(e) => {
            if (!hasActiveChild) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = S.textNorm;
            }
          }}
        >
          {GroupIcon && <GroupIcon size={20} />}
        </button>

        {/* Popover */}
        {isPopoverOpen && (
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              left: 80,
              top: triggerRef.current?.getBoundingClientRect().top ?? 0,
              minWidth: 180,
              background: "#1E293B",
              borderRadius: RADIUS.md,
              boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
              padding: SP[2],
              zIndex: 30,
            }}
          >
            <div
              style={{
                fontSize: TYPE.xs,
                color: S.accent,
                fontWeight: TYPE.bold,
                textTransform: "uppercase",
                letterSpacing: "1px",
                padding: `${SP[1]} ${SP[2]}`,
                marginBottom: SP[1],
                fontFamily: TYPE.fontSans,
              }}
            >
              {group.label}
            </div>
            {visibleChildren.map((child) => {
              const ChildIcon = child.Icon;
              const on = active === child.id;
              return (
                <button
                  key={child.id}
                  onClick={() => navigateTo(child.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: SP[2],
                    padding: `${SP[2]} ${SP[2]}`,
                    borderRadius: RADIUS.sm,
                    border: "none",
                    cursor: "pointer",
                    background: on ? S.accent : "transparent",
                    color: on ? S.textActive : S.textNorm,
                    fontWeight: on ? TYPE.bold : TYPE.medium,
                    fontSize: TYPE.sm,
                    fontFamily: TYPE.fontSans,
                    transition: TRANSITION.fast,
                    outline: "none",
                    marginBottom: "1px",
                  }}
                  onMouseEnter={(e) => {
                    if (!on) {
                      e.currentTarget.style.background = S.cardBg;
                      e.currentTarget.style.color = S.textHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!on) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = S.textNorm;
                    }
                  }}
                >
                  {ChildIcon && <ChildIcon size={16} />}
                  <span>{child.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Expanded mode: accordion ──────────────────────
  return (
    <div style={{ marginBottom: "2px" }} data-tour={`nav-group-${group.id}`}>
      {/* Group header */}
      <button
        onClick={() => toggleGroup(group.id)}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "Recolher" : "Expandir"} ${group.label}`}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: SP[3],
          padding: `${SP[2]} ${SP[3]}`,
          borderRadius: RADIUS.sm,
          border: "none",
          cursor: "pointer",
          background: hasActiveChild ? S.groupActive : "transparent",
          color: hasActiveChild ? S.accent : S.textNorm,
          fontWeight: TYPE.semibold,
          fontSize: TYPE.base,
          fontFamily: TYPE.fontSans,
          transition: TRANSITION.fast,
          outline: "none",
        }}
        onMouseEnter={(e) => {
          if (!hasActiveChild) {
            e.currentTarget.style.background = S.cardBg;
            e.currentTarget.style.color = S.textHover;
          }
        }}
        onMouseLeave={(e) => {
          if (!hasActiveChild) {
            e.currentTarget.style.background = hasActiveChild ? S.groupActive : "transparent";
            e.currentTarget.style.color = hasActiveChild ? S.accent : S.textNorm;
          }
        }}
        onFocus={(e) => {
          e.currentTarget.style.outline = `2px solid ${S.accent}`;
          e.currentTarget.style.outlineOffset = "-2px";
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = "none";
        }}
      >
        {GroupIcon && <GroupIcon size={18} />}
        <span style={{ flex: 1, textAlign: "left" }}>{group.label}</span>
        <IconChevronDown
          size={14}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            opacity: 0.5,
          }}
        />
      </button>

      {/* Children (with smooth height transition) */}
      <div
        style={{
          overflow: "hidden",
          maxHeight: isOpen ? `${visibleChildren.length * 40}px` : "0px",
          transition: "max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {visibleChildren.map((child) => (
          <NavItem
            key={child.id}
            item={child}
            active={active === child.id}
            col={false}
            onClick={() => navigateTo(child.id)}
            indent
          />
        ))}
      </div>
    </div>
  );
}
SidebarGroup.propTypes = {
  group: PropTypes.object.isRequired,
  active: PropTypes.string,
  col: PropTypes.bool,
  openGroups: PropTypes.object.isRequired,
  toggleGroup: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  isVisible: PropTypes.func.isRequired,
  popoverGroup: PropTypes.string,
  setPopoverGroup: PropTypes.func.isRequired,
  popoverRef: PropTypes.object.isRequired,
  popoverTriggerRef: PropTypes.object.isRequired,
};

// ═══════════════════════════════════════════════════════
// SIDEBAR BUTTON — footer utility buttons
// ═══════════════════════════════════════════════════════
function SidebarBtn({
  onClick,
  col,
  icon,
  label,
  badge = 0,
  ariaLabel,
  last = false,
  isActive = false,
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel || label}
      style={{
        width: "100%",
        padding: col ? `${SP[2]} 0` : `${SP[2]} ${SP[3]}`,
        borderRadius: RADIUS.sm,
        border: `1px solid ${S.divider}`,
        background: isActive ? S.accent : "transparent",
        color: isActive ? S.textActive : S.textNorm,
        cursor: "pointer",
        fontSize: TYPE.xs,
        fontWeight: isActive ? TYPE.bold : TYPE.semibold,
        fontFamily: TYPE.fontSans,
        display: "flex",
        alignItems: "center",
        justifyContent: col ? "center" : "flex-start",
        gap: SP[2],
        marginBottom: last ? 0 : SP[1],
        position: "relative",
        transition: TRANSITION.fast,
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = S.cardBg;
          e.currentTarget.style.color = S.textHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = S.textNorm;
        }
      }}
    >
      {icon}
      {!col && label}
      {badge > 0 && (
        <span
          aria-label={`${badge} não lidas`}
          style={{
            position: col ? "absolute" : "static",
            top: col ? 4 : undefined,
            right: col ? 4 : undefined,
            marginLeft: col ? undefined : "auto",
            minWidth: 18,
            height: 18,
            borderRadius: RADIUS.full,
            background: S.badge,
            color: "#fff",
            fontSize: "0.625rem",
            fontWeight: TYPE.heavy,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px",
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
SidebarBtn.propTypes = {
  onClick: PropTypes.func,
  col: PropTypes.bool,
  icon: PropTypes.node,
  label: PropTypes.string,
  badge: PropTypes.number,
  ariaLabel: PropTypes.string,
  last: PropTypes.bool,
  isActive: PropTypes.bool,
};

Sidebar.propTypes = {
  active: PropTypes.string.isRequired,
  setActive: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  col: PropTypes.bool.isRequired,
  setCol: PropTypes.func.isRequired,
  dark: PropTypes.bool.isRequired,
  setDark: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
  client: PropTypes.object,
  notifications: PropTypes.array,
  onNotifClick: PropTypes.func.isRequired,
  mobileOpen: PropTypes.bool.isRequired,
  setMobileOpen: PropTypes.func.isRequired,
  privacyMode: PropTypes.bool,
  setPrivacyMode: PropTypes.func,
};
